import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class NotificationSoundService implements OnDestroy {
  private readonly soundPath = 'assets/sounds/sound.wav';
  private readonly unlockEvents: Array<keyof DocumentEventMap> = ['pointerdown', 'touchstart', 'keydown'];
  private readonly handleFirstUserInteraction = () => {
    void this.unlockAudio();
  };

  private audio: HTMLAudioElement | null = null;
  private listenersBound = false;
  private unlocked = false;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    const AudioCtor = this.document.defaultView?.Audio;
    if (!AudioCtor) {
      return;
    }

    this.audio = new AudioCtor(this.soundPath);
    this.audio.preload = 'auto';
    this.bindUnlockListeners();
  }

  ngOnDestroy(): void {
    this.removeUnlockListeners();
  }

  play(): void {
    if (!this.audio) {
      return;
    }

    this.audio.currentTime = 0;
    this.audio.play().catch(error => {
      if (this.unlocked) {
        console.warn('Unable to play notification sound:', error);
      }
    });
  }

  private bindUnlockListeners(): void {
    if (this.listenersBound) {
      return;
    }

    this.unlockEvents.forEach(eventName => {
      this.document.addEventListener(eventName, this.handleFirstUserInteraction, {
        capture: true,
        passive: true,
      });
    });
    this.listenersBound = true;
  }

  private removeUnlockListeners(): void {
    if (!this.listenersBound) {
      return;
    }

    this.unlockEvents.forEach(eventName => {
      this.document.removeEventListener(eventName, this.handleFirstUserInteraction, true);
    });
    this.listenersBound = false;
  }

  private async unlockAudio(): Promise<void> {
    if (!this.audio) {
      return;
    }

    try {
      this.audio.muted = true;
      await this.audio.play();
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio.muted = false;
      this.unlocked = true;
      this.removeUnlockListeners();
    } catch {
      this.audio.muted = false;
    }
  }
}
